import React, { useMemo, useState } from "react";
import { formatTreeHeaderLabel, getNestedValue, setNestedValue } from "./TestCaseHelpers";
import { InfoIcon, ChevronDownSmIcon } from "./svgs/TestCaseSvgs";

interface TelecommandParamPanelProps {
  tcJsonText: string;
  onUpdateJson: (text: string) => void;
}

interface ParamItem {
  key: string;
  displayName: string;
  value: unknown;
}

interface TreeNode {
  label: string;
  fullPath: string;
  item?: ParamItem;
  children: TreeNode[];
}

const flattenJsonLeaves = (obj: unknown, prefix = ""): ParamItem[] => {
  if (Array.isArray(obj)) {
    return obj.flatMap((item, index) => {
      const key = `${prefix}[${index}]`;
      if (item !== null && typeof item === "object") {
        return flattenJsonLeaves(item, key);
      }
      return [{ key, displayName: key, value: item }];
    });
  }

  if (obj !== null && typeof obj === "object") {
    return Object.entries(obj as Record<string, unknown>).flatMap(([field, value]) => {
      const key = prefix ? `${prefix}.${field}` : field;
      if (value !== null && typeof value === "object") {
        return flattenJsonLeaves(value, key);
      }
      return [{ key, displayName: key, value }];
    });
  }

  if (!prefix) return [];
  return [{ key: prefix, displayName: prefix, value: obj }];
};

const toEditableText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  return String(value);
};

// Splits a key like "items[0].field" into [{label:"items",path:"items"}, {label:"[0]",path:"items[0]"}, {label:"field",path:"items[0].field"}]
const getPathSegments = (key: string): { label: string; path: string }[] => {
  const result: { label: string; path: string }[] = [];
  let remaining = key;
  let currentPath = "";
  while (remaining.length > 0) {
    const dotMatch = remaining.match(/^\.?([^.\[]+)/);
    if (dotMatch) {
      const label = dotMatch[1];
      currentPath = currentPath ? `${currentPath}.${label}` : label;
      result.push({ label, path: currentPath });
      remaining = remaining.slice(dotMatch[0].length);
      continue;
    }
    const arrMatch = remaining.match(/^\[(\d+)\]/);
    if (arrMatch) {
      const label = `[${arrMatch[1]}]`;
      currentPath = `${currentPath}${label}`;
      result.push({ label, path: currentPath });
      remaining = remaining.slice(arrMatch[0].length);
      continue;
    }
    break;
  }
  return result;
};

const buildTree = (params: ParamItem[]): TreeNode[] => {
  const nodeByPath = new Map<string, TreeNode>();

  const getOrCreate = (path: string, label: string): TreeNode => {
    if (!nodeByPath.has(path)) {
      nodeByPath.set(path, { label, fullPath: path, children: [] });
    }
    return nodeByPath.get(path)!;
  };

  params.forEach((param) => {
    const segments = getPathSegments(param.key);
    if (segments.length === 0) return;
    for (let i = 0; i < segments.length - 1; i++) {
      getOrCreate(segments[i].path, segments[i].label);
    }
    const leaf = segments[segments.length - 1];
    const node = getOrCreate(leaf.path, leaf.label);
    node.item = param;
  });

  const childPaths = new Set<string>();
  nodeByPath.forEach((node, path) => {
    const segments = getPathSegments(path);
    if (segments.length > 1) {
      const parentPath = segments[segments.length - 2].path;
      const parent = nodeByPath.get(parentPath);
      if (parent) {
        if (!parent.children.some((c) => c.fullPath === path)) {
          parent.children.push(node);
        }
        childPaths.add(path);
      }
    }
  });

  return Array.from(nodeByPath.values()).filter((n) => !childPaths.has(n.fullPath));
};

const parseInputValue = (raw: string, currentValue: unknown): unknown => {
  if (typeof currentValue === "number") {
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? currentValue : parsed;
  }
  if (typeof currentValue === "boolean") {
    return raw === "true";
  }
  return raw;
};

const TelecommandParamPanel = ({ tcJsonText, onUpdateJson }: TelecommandParamPanelProps): JSX.Element => {
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());

  const toggleFolderCollapsed = (fullPath: string): void => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(fullPath)) next.delete(fullPath);
      else next.add(fullPath);
      return next;
    });
  };

  const parsedJson = useMemo(() => {
    try {
      return JSON.parse(tcJsonText);
    } catch {
      return null;
    }
  }, [tcJsonText]);

  const params = useMemo(() => {
    if (!parsedJson || typeof parsedJson !== "object") return [];
    return flattenJsonLeaves(parsedJson);
  }, [parsedJson]);

  const treeNodes = useMemo(() => buildTree(params), [params]);

  const handleValueChange = (key: string, raw: string): void => {
    if (!parsedJson || typeof parsedJson !== "object") return;
    const next = JSON.parse(JSON.stringify(parsedJson)) as Record<string, unknown>;
    const currentValue = getNestedValue(parsedJson, key);
    const nextValue = parseInputValue(raw, currentValue);
    setNestedValue(next, key, nextValue);
    onUpdateJson(JSON.stringify(next, null, 2));
  };

  if (!parsedJson || typeof parsedJson !== "object") {
    return (
      <div className="tcd-tc-form-empty">
        <InfoIcon />
        <span>Fix invalid JSON to use form view.</span>
      </div>
    );
  }

  if (params.length === 0) {
    return (
      <div className="tcd-tc-form-empty">
        <InfoIcon />
        <span>No editable fields found in telecommand JSON.</span>
      </div>
    );
  }

  const renderTreeNodes = (nodes: TreeNode[], depth: number): JSX.Element[] =>
    nodes.flatMap((node): JSX.Element[] => {
      const indent = depth * 16;
      const isNested = depth > 0;

      if (!node.item) {
        const isCollapsed = collapsedFolders.has(node.fullPath);
        return [
          <div key={`folder-${node.fullPath}`} className="tcd-tree-section">
            <div className={`tcd-tree-folder${isNested ? ' tcd-tree-node' : ''}${!isCollapsed ? ' expanded' : ''}`} style={{ paddingLeft: indent }}>
              <button
                type="button"
                className="tcd-folder-toggle-btn"
                onClick={() => toggleFolderCollapsed(node.fullPath)}
              >
                <ChevronDownSmIcon className={isCollapsed ? "collapsed" : ""} />
                <span className="tcd-tree-folder__label">{formatTreeHeaderLabel(node.label)}</span>
              </button>
            </div>
            {!isCollapsed && (
              <div
                className="tcd-tree-children-wrap"
                style={{ '--tree-indent': `${indent}px` } as React.CSSProperties}
              >
                {renderTreeNodes(node.children, depth + 1)}
              </div>
            )}
          </div>,
        ];
      }

      const { item } = node;
      const isBoolean = typeof item.value === "boolean";
      const results: JSX.Element[] = [
        <div key={item.key} className={`tcd-tc-form-row${isNested ? ' tcd-tree-node' : ''}`} style={{ paddingLeft: indent }}>
          <label className="tcd-tc-form-row__label" title={item.key}>{node.label}</label>
          <div className="tcd-tc-form-row__input-wrap">
            {isBoolean ? (
              <select
                className="tcd-tc-form-row__input"
                value={String(item.value)}
                onChange={(e) => handleValueChange(item.key, e.target.value)}
              >
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            ) : (
              <input
                className="tcd-tc-form-row__input"
                type={typeof item.value === "number" ? "number" : "text"}
                value={toEditableText(item.value)}
                onChange={(e) => handleValueChange(item.key, e.target.value)}
              />
            )}
          </div>
        </div>,
      ];

      if (node.children.length > 0) {
        results.push(
          <div
            key={`${node.fullPath}-children`}
            className="tcd-tree-children-wrap"
            style={{ '--tree-indent': `${indent}px` } as React.CSSProperties}
          >
            {renderTreeNodes(node.children, depth + 1)}
          </div>
        );
      }

      return results;
    });

  return (
    <div className="tcd-tc-form-panel">
      {renderTreeNodes(treeNodes, 0)}
    </div>
  );
};

export default TelecommandParamPanel;
